import type { ReconciliationResult, Transaction } from "../types";

/**
 * The product's core trust guarantee: opening balance + Σ(signed amounts)
 * must equal the closing balance. If it reconciles, the parse is provably
 * complete and correct — no LLM competitor can offer this.
 */
export function reconcile(
  transactions: Transaction[],
  openingBalance: number | null,
  closingBalance: number | null,
  tolerance = 0.01,
): ReconciliationResult {
  const sum = transactions.reduce((acc, t) => acc + t.amount, 0);

  if (openingBalance == null || closingBalance == null) {
    return {
      ok: false,
      openingBalance,
      closingBalance,
      computedClosing: openingBalance == null ? null : round2(openingBalance + sum),
      difference: null,
      tolerance,
      message:
        "Не удалось определить начальный/конечный баланс — проверка не выполнена.",
    };
  }

  const computedClosing = round2(openingBalance + sum);
  const difference = round2(closingBalance - computedClosing);
  const ok = Math.abs(difference) <= tolerance;

  return {
    ok,
    openingBalance,
    closingBalance,
    computedClosing,
    difference,
    tolerance,
    message: ok
      ? "✓ Баланс сошёлся: начальный + сумма транзакций = конечный."
      : `✗ Расхождение ${difference.toFixed(2)}: возможно, пропущены или неверно распознаны транзакции.`,
  };
}

/**
 * A secondary, per-row consistency check available when the statement prints a
 * running balance: each row's balance should equal the previous balance plus
 * the row amount. Returns the indices of rows that break the chain.
 */
export function runningBalanceBreaks(
  transactions: Transaction[],
  tolerance = 0.01,
): number[] {
  const breaks: number[] = [];
  for (let i = 1; i < transactions.length; i++) {
    const prev = transactions[i - 1]!;
    const cur = transactions[i]!;
    if (prev.balance == null || cur.balance == null) continue;
    const expected = round2(prev.balance + cur.amount);
    if (Math.abs(round2(cur.balance) - expected) > tolerance) {
      breaks.push(i);
    }
  }
  return breaks;
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}
